import { Collapse, Tooltip } from '@material-ui/core';
import { formatVehicleType } from '@superdispatch/sdk';
import { Column, Columns, Stack } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { forwardRef } from 'react';
import { VehicleConditionIcon } from 'shared/ui/VehicleConditionIcon';
import styled from 'styled-components';
import {
  findVehicleSamplePhoto,
  formatVehicleTitle,
  PostingTransportType,
  PostingVehicleDTO,
} from '../../data/dto/PostingLoadDTO';
import { PostingVehicleDimensions } from './PostingVehicleDimensions';
import { VehicleImageViewer } from './VehicleImageViewer';

const DetailsWrapper = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row-reverse;

  & > div:nth-child(2) {
    flex: 1;
  }
`;

interface PostingVehicleProps {
  vehicle: PostingVehicleDTO;
  showDetails?: boolean;
  transportType?: PostingTransportType;
  hideVehicleInfo?: boolean;
}

export const PostingVehicle = forwardRef<HTMLDivElement, PostingVehicleProps>(
  ({ vehicle, showDetails, transportType, hideVehicleInfo = false }, ref) => {
    const title = formatVehicleTitle(vehicle, { hideVehicleInfo });
    const vehicleType = formatVehicleType(vehicle.type, { fallback: '' });
    const samplePhoto = findVehicleSamplePhoto(vehicle);

    return (
      <Stack ref={ref} space={showDetails ? 'xsmall' : 'none'}>
        <Columns space="xxsmall" align="center">
          <Column width="adaptive">
            <Tooltip
              enterDelay={800}
              title={
                <Stack>
                  <TextBox color="white" variant="heading-5">
                    {title}
                  </TextBox>

                  <PostingVehicleDimensions color="white" vehicle={vehicle} />
                </Stack>
              }
            >
              <TextBox
                noWrap={true}
                aria-label="Vehicle title"
                variant={
                  hideVehicleInfo || showDetails ? 'body-semibold' : 'body'
                }
              >
                {title}
              </TextBox>
            </Tooltip>
          </Column>

          {vehicleType && (
            <Column width="content">
              <TextBox
                color="secondary"
                aria-label="Vehicle type"
                variant={showDetails ? 'body-semibold' : 'body'}
              >
                {vehicleType}
              </TextBox>
            </Column>
          )}

          <Column width="content">
            <VehicleConditionIcon
              transportType={transportType}
              isInoperable={vehicle.is_inoperable}
            />
          </Column>
        </Columns>

        <Collapse in={showDetails}>
          <DetailsWrapper>
            {!!samplePhoto?.photo_url && (
              <VehicleImageViewer url={samplePhoto.photo_url} />
            )}

            <PostingVehicleDimensions vehicle={vehicle} />
          </DetailsWrapper>
        </Collapse>
      </Stack>
    );
  },
);

PostingVehicle.displayName = 'PostingVehicle';
